/**
 * A modal window that stops the page until it is answered, built on the native `<dialog>` and
 * `showModal()`. The browser makes everything behind it inert, keeps Tab inside, closes it on
 * Escape and puts focus back on the control that opened it (WAI-ARIA dialog pattern).
 *
 * The caller owns `open`. Escape and the close button call `onClose`, and the caller decides whether
 * to close, so a dialog that is saving or has unsaved changes can refuse. It has no `dirty` guard
 * of its own: a form with typed input belongs in `SideSheet`, which asks before it discards. The content mounts only
 * while open, so a form inside starts empty each time.
 *
 * Focus starts on the control marked `initialFocus` (a Button prop) or else the first control. A
 * dialog that destroys something marks its Cancel button so Enter never confirms by accident.
 *
 * Below 640px it fills the screen, since a floating box on a phone leaves the page behind it
 * tappable-looking and the content cramped (ux.md S8). `variant="sheet"` docks it to the end edge
 * instead, as `SideSheet` does, with the body scrolling between a header and a footer that stay put.
 * It does not close on a click outside: a stray tap should not throw away what was typed.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { CloseIcon } from "./icons.tsx";
import { IconButton } from "./IconButton.tsx";
import { Heading } from "./Text.tsx";
const arrive = "xrngkwq-B";
const slideIn = "x1134vkj-B";
const fade = "x18re5ia-B";
const styles = {
  dialog: {
    kogj98: "x1bpp3o7 x1ekcn5q",
    ks0D6T: "x16xn7b0",
    kmVPX3: "x1717udv",
    kskxy: "xs8sm83 xzdtdkp",
    kVQacm: "xb3r6kr",
    kMzoRj: "xdq7b4a x14o5g9r",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xv9mh3h",
    kaIpWk: "x1gzjth6 x1ojxr52",
    kWkggS: "x40k5rp",
    kMwMTN: "xm8xz4y",
    kGVxlE: "x176vcc5",
    kGyWv1: "x1j13seq",
    kKVMdj: "xv8cai x16k6u9l",
    k44tkh: "xcdydla",
    kyAemX: "x1ip1z55",
    $$css: true
  },
  sm: {
    kzqmXN: "x1kinsug x1f9mggg",
    $$css: true
  },
  md: {
    kzqmXN: "x19ycldx x1f9mggg",
    $$css: true
  },
  lg: {
    kzqmXN: "x13msm1j x1f9mggg",
    $$css: true
  },
  phoneFull: {
    kZKoxP: "xg7h5cd xl6nyy7",
    $$css: true
  },
  phoneBottom: {
    kZKoxP: "xg7h5cd",
    kaIpWk: "x1gzjth6 x1mgdnre",
    $$css: true
  },
  sheet: {
    keTefX: "xvc5jky",
    k71WvV: "x14z9mp",
    kqGvvJ: "x10im51j",
    kzqmXN: "x38i08l x1f9mggg",
    kZKoxP: "xtdtrs8",
    kskxy: "x1wj9ous",
    kaIpWk: "x2u8bby",
    kLoX6v: "x1616tdu",
    ke9TFa: "x10w94by",
    kKVMdj: "xpfqwoh x16k6u9l",
    $$css: true
  },
  frame: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kZKoxP: "x5yr21d",
    kskxy: "xedcshv",
    $$css: true
  },
  head: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x626il7",
    kLKAdn: "xjh1bw0",
    kg3NbH: "x179o8vp",
    kGO01o: "x1ovbggj",
    $$css: true
  },
  headScrolled: {
    kt9PQ7: "xq3vdme",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "x4a0p42",
    $$css: true
  },
  body: {
    kzQI83: "x1iyjqo2",
    kAzted: "x2lwn1j",
    kORKVm: "x1odjw0f",
    kg3NbH: "x179o8vp",
    kGO01o: "x42188t",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  foot: {
    k1xSpc: "x78zum5",
    kXwgrk: "x1q0g3np x6phar",
    kGNEyG: "x13iwyz6 x176h1w7",
    kwnvtZ: "x1a02dak",
    kjj79g: "x13a6bvl",
    kOIVth: "x1173whf",
    k8WAf4: "xp1t01x",
    kg3NbH: "x179o8vp",
    kEafiO: "x153ygsw",
    kPef9Z: "x13fuv20",
    kLZC3w: "x9wgt4q",
    kWkggS: "x40k5rp",
    $$css: true
  }
};
export interface DialogProps {
  readonly open: boolean;
  /** Escape and the close button ask for this. The caller may ignore it. */
  readonly onClose: () => void;
  /** What the window is about, as its heading and its name. */
  readonly title: string;
  readonly size?: "sm" | "md" | "lg";
  readonly variant?: "dialog" | "sheet";
  /** A close button at the top right. On by default for a sheet, off for a dialog with Cancel. */
  readonly closeLabel?: string;
  readonly footer?: ReactNode;
  readonly testId?: string | undefined;
  readonly children: ReactNode;
}
export function Dialog({
  open,
  onClose,
  title,
  size: width = "md",
  variant = "dialog",
  closeLabel,
  footer,
  testId,
  children
}: DialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const sheet = variant === "sheet";
  const [scrolled, setScrolled] = useState(false);

  /* A layout effect, so the browser closes it and moves focus back before the key press ends. */
  useLayoutEffect(() => {
    const element = dialog.current;
    if (element === null) return;
    if (open && !element.open) {
      setScrolled(false);
      element.showModal();
      element.querySelector<HTMLElement>('[data-initial-focus="true"]')?.focus();
    } else if (!open && element.open) element.close();
  }, [open]);

  /* Closes the element when the caller closes it, and also if this unmounts while open. */
  useEffect(() => {
    const element = dialog.current;
    return () => {
      if (element?.open === true) element.close();
    };
  }, []);
  return <dialog ref={dialog} aria-labelledby={headingId} data-testid={testId}
  /* Chrome closes a dialog on a second Escape that has no click or key between it and the first,
     and that close cannot be cancelled. Handling the key itself stops it getting that far. */ onKeyDown={event => {
    if (event.key !== "Escape") return;

    /* A menu or popover open inside the dialog takes this Escape and closes alone. */
    if (event.target instanceof Element && event.target.closest(":popover-open") !== null) return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  }}
  /* If the element was closed anyway while the caller still has it open, it is shown again. */ onClose={() => {
    const element = dialog.current;
    if (open && element !== null && !element.open) element.showModal();
  }} onCancel={event => {
    /* The browser would close it. The caller decides, because it may be saving or unsaved. */
    event.preventDefault();
    onClose();
  }} {...stylex.props(styles.dialog, sheet ? styles.sheet : [styles[width], width === "sm" ? styles.phoneBottom : styles.phoneFull])}>
      {open && <div className="x78zum5 xdt5ytf x5yr21d xedcshv">
          <div {...{
        0: {
          className: "x78zum5 x6s0dn4 x1qughib x626il7 xjh1bw0 x179o8vp x1ovbggj"
        },
        1: {
          className: "x78zum5 x6s0dn4 x1qughib x626il7 xjh1bw0 x179o8vp x1ovbggj xq3vdme x1q0q8m5 x4a0p42"
        }
      }[!!scrolled << 0]}>
            <Heading level={2} size="pageTitleCompact" id={headingId}>
              {title}
            </Heading>
            {closeLabel !== undefined && <IconButton label={closeLabel} icon={<CloseIcon />} onClick={onClose} tipAlign="end" />}
          </div>
          <div onScroll={event => setScrolled(event.currentTarget.scrollTop > 0)} className="x1iyjqo2 x2lwn1j x1odjw0f x179o8vp x42188t x78zum5 xdt5ytf x626il7">
            {children}
          </div>
          {footer !== undefined && <div className="x78zum5 x1q0g3np x6phar x13iwyz6 x176h1w7 x1a02dak x13a6bvl x1173whf xp1t01x x179o8vp x153ygsw x13fuv20 x9wgt4q x40k5rp">{footer}</div>}
        </div>}
    </dialog>;
}